<script lang="ts" setup>
import type { ContactUsConfig } from '../configs/types'

const props = defineProps<{
  config: ContactUsConfig
  des: string
  email: string
  loading: boolean
  handleConfirm: () => void
}>()

const emit = defineEmits<{
  (event: 'update:des', value: string): void
  (event: 'update:email', value: string): void
}>()

function updateField(field: 'des' | 'email', event: Event) {
  emit(`update:${field}`, (event.target as HTMLInputElement | HTMLTextAreaElement).value)
}

const bgPrimary = props.config.colors.bgPrimary
const bgTertiary = props.config.colors.bgTertiary
const textPrimary = props.config.colors.textPrimary
const textTertiary = props.config.colors.textTertiary
const textPlaceholder = props.config.colors.textPlaceholder
const accentRed = props.config.colors.accentRed
const primaryColor = props.config.colors.primaryColor
const whiteColor = props.config.colors.whiteColor
</script>

<template>
  <div class="contact-us-page">
    <div class="h-44px">
      <BasicNavbar opacity class="color-n-text-2" />
    </div>
    <div class="form-container">
      <div class="form-item">
        <div class="form-title">
          <span class="required">*</span>{{ config.texts.feedbackTitle }}
        </div>
        <div class="textarea-wrapper">
          <textarea
            :value="des"
            class="form-textarea"
            :placeholder="config.texts.feedbackPlaceholder"
            maxlength="100"
            @input="updateField('des', $event)"
          />
          <div class="char-count">
            {{ des.length }}/100
          </div>
        </div>
      </div>

      <div class="form-item">
        <div class="form-title">
          <span class="required">*</span>{{ config.texts.emailTitle }}
        </div>
        <input
          :value="email"
          type="text"
          class="form-input"
          :placeholder="config.texts.emailPlaceholder"
          @input="updateField('email', $event)"
        >
      </div>

      <button
        :disabled="des === '' || email === '' || loading"
        class="submit-btn"
        :class="{ 'submit-btn-loading': loading }"
        @click="handleConfirm"
      >
        {{ loading ? config.texts.submittingButton : config.texts.submitButton }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.contact-us-page {
  min-height: 100vh;
  background: v-bind(bgPrimary);
}

.form-container {
  padding: 0 10px;
}

.form-item {
  margin-bottom: 20px;
}

.form-title {
  padding: 18px 0;
  display: flex;
  gap: 5px;
  align-items: center;
  font-size: 16px;
  color: v-bind(textPrimary);

  .required {
    color: v-bind(accentRed);
  }
}

.textarea-wrapper {
  position: relative;
}

.form-textarea {
  width: 100%;
  height: 130px;
  background: v-bind(bgTertiary);
  border-radius: 8px;
  border: none;
  padding: 12px;
  font-size: 14px;
  color: v-bind(textPrimary);
  resize: none;
  box-sizing: border-box;
  font-family: inherit;

  &::placeholder {
    color: v-bind(textPlaceholder);
  }

  &:focus {
    outline: none;
  }
}

.char-count {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: v-bind(textTertiary);
}

.form-input {
  width: 100%;
  height: 44px;
  background: v-bind(bgTertiary);
  border-radius: 8px;
  border: none;
  padding: 0 12px;
  font-size: 14px;
  color: v-bind(textPrimary);
  box-sizing: border-box;

  &::placeholder {
    color: v-bind(textPlaceholder);
  }

  &:focus {
    outline: none;
  }
}

.submit-btn {
  display: block;
  margin: 40px auto 0;
  width: 265px;
  height: 40px;
  border-radius: 60px;
  background: v-bind(primaryColor);
  color: v-bind(whiteColor);
  font-size: 16px;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    opacity: 0.8;
  }

  &-loading {
    opacity: 0.8;
  }
}
</style>
